import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  MapPin, CheckCircle, Loader2, AlertCircle, 
  Smartphone, Navigation, RefreshCw, Signal
} from 'lucide-react';
import salesApiClient from '@/api/salesClient';

export default function MobileTrackerPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('initializing'); // initializing, tracking, success, error
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  
  // Get user token from URL params or localStorage
  const token = searchParams.get('token') || localStorage.getItem('salesToken') || localStorage.getItem('authToken');
  const userId = searchParams.get('uid');
  const userName = searchParams.get('name') || 'Sales Rep';

  useEffect(() => {
    if (token) {
      startTracking();
    }
  }, [token]);

  const startTracking = () => {
    if (!navigator.geolocation) {
      setStatus('error');
      setError('Geolocation is not supported by your browser');
      return;
    }

    setStatus('tracking');

    // Get location immediately
    updateLocation();

    // Then update every 2 minutes
    const interval = setInterval(updateLocation, 120000);
    
    return () => clearInterval(interval);
  };

  const updateLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocation({ latitude, longitude, accuracy });
        setLastUpdate(new Date());
        setUpdateCount(prev => prev + 1);

        // Send to server
        try {
          await sendLocationToServer(latitude, longitude);
          setStatus('success');
        } catch (err) {
          console.error('Failed to send location:', err);
          setStatus('success'); // Still show success for location, just failed to send
        }
      },
      (err) => {
        setStatus('error');
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Please allow location access to enable tracking');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location information unavailable');
            break;
          case err.TIMEOUT:
            setError('Location request timed out');
            break;
          default:
            setError('An unknown error occurred');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const sendLocationToServer = async (latitude, longitude) => {
    // Get address from coordinates (reverse geocoding)
    let locationName = 'Current Location';
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );
      const data = await response.json();
      if (data.display_name) {
        // Extract short address
        const parts = data.display_name.split(',');
        locationName = parts.slice(0, 3).join(',');
      }
    } catch (e) {
      console.log('Could not get address');
    }

    // Send to backend
    const headers = { 'Authorization': `Bearer ${token}` };
    await salesApiClient.post('/team/locations/update', {
      user_id: userId || 'unknown',
      latitude,
      longitude,
      location: locationName,
      status: 'online',
      timestamp: new Date().toISOString()
    }, { headers });
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'tracking': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/80 border-slate-700 backdrop-blur-sm">
        <CardContent className="p-6 text-center">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
              alt="Aurous" 
              className="h-10 mx-auto mb-2"
            />
            <p className="text-slate-400 text-sm">MySales Orbit - Location Tracker</p>
          </div>

          {/* Status Icon */}
          <div className={`w-24 h-24 rounded-full ${getStatusColor()} mx-auto mb-6 flex items-center justify-center shadow-lg`}>
            {status === 'initializing' && <Loader2 className="w-12 h-12 text-white animate-spin" />}
            {status === 'tracking' && <Navigation className="w-12 h-12 text-white animate-pulse" />}
            {status === 'success' && <CheckCircle className="w-12 h-12 text-white" />}
            {status === 'error' && <AlertCircle className="w-12 h-12 text-white" />}
          </div>

          {/* User Name */}
          <h1 className="text-xl font-bold text-white mb-2">
            Hi, {userName}!
          </h1>

          {/* Status Message */}
          <div className="mb-6">
            {status === 'initializing' && (
              <p className="text-slate-300">Initializing location tracker...</p>
            )}
            {status === 'tracking' && (
              <p className="text-yellow-400">Getting your location...</p>
            )}
            {status === 'success' && (
              <div>
                <Badge className="bg-green-500/20 text-green-400 border-0 mb-2">
                  <Signal className="w-3 h-3 mr-1" /> Location Shared
                </Badge>
                <p className="text-slate-300 text-sm">Your location is being tracked</p>
              </div>
            )}
            {status === 'error' && (
              <div>
                <Badge className="bg-red-500/20 text-red-400 border-0 mb-2">Error</Badge>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Location Info */}
          {location && (
            <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Current Location</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Latitude</p>
                  <p className="text-white font-mono">{location.latitude.toFixed(6)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Longitude</p>
                  <p className="text-white font-mono">{location.longitude.toFixed(6)}</p>
                </div>
              </div>
              {location.accuracy && (
                <p className="text-slate-500 text-xs mt-2">
                  Accuracy: ±{Math.round(location.accuracy)}m
                </p>
              )}
            </div>
          )}

          {/* Last Update */}
          {lastUpdate && (
            <p className="text-slate-500 text-xs mb-4">
              Last updated: {lastUpdate.toLocaleTimeString()} ({updateCount} updates)
            </p>
          )}

          {/* Refresh Button */}
          <Button 
            onClick={updateLocation}
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={status === 'tracking'}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${status === 'tracking' ? 'animate-spin' : ''}`} />
            Update Location Now
          </Button>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-slate-700/30 rounded-xl">
            <p className="text-slate-400 text-xs">
              <Smartphone className="w-4 h-4 inline mr-1" />
              Keep this page open to share your location with the team. 
              Add to home screen for quick access.
            </p>
          </div>

          {/* Error Recovery */}
          {status === 'error' && (
            <Button 
              variant="outline" 
              className="w-full mt-4 border-slate-600 text-slate-300"
              onClick={() => {
                setStatus('initializing');
                setError(null);
                startTracking();
              }}
            >
              Try Again
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
